import { Component, OnInit, ViewChild } from '@angular/core';
import { formatDate, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GearSelectDialogComponent } from '../gear-select-dialog/gear-select-dialog.component';
import { FishingEvent } from '../shared/fishing-event.model';
import { LocalStorageService, StorageResult } from '../shared/local-storage.service';
import { Subscription } from 'rxjs';
import { AbstractControl, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { Gear } from '../shared/gear.model';
import { GeolocationService } from '@ng-web-apis/geolocation';
import { DateTimePickerComponent } from '../date-time-picker/date-time-picker.component';
import { PotGear } from '../shared/pot-gear.model';
import { LonglineGear } from '../shared/longline-gear.model';
import { PelagicGear } from '../shared/pelagic-gear.model';
import { AddCatchDialogComponent } from '../add-catch-dialog/add-catch-dialog.component';
import { ConfirmDialogComponent } from '../vessel-reg/confirm-dialog/confirm-dialog.component';
import { Catch } from '../shared/catch.model';
import { MatTable } from '@angular/material/table';
import { setHaulDateValidator, maxDateValidator, coordValidator, gearValidator, skateNumLostPotValidator, shapefileLatValidator, shapefileLonValidator } from '../shared/trip-validator.model';
import { Coordinate } from '../shared/coordinate.model';
import { CoordinateDialogComponent } from './coordinate-dialog/coordinate-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { ErrorStateMatcher } from '@angular/material/core';
import { MenuComponent } from '../menu/menu.component';
import { Trip } from '../shared/trip.model';
import { Insights } from '../shared/insights.service';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && (form?.errors?.invalid || control.hasError('required')) && (control.dirty || control.touched || isSubmitted));
  }
}


let SCREEN_WIDTH: string =  Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0).toString() + 'px';
let SCREEN_HEIGHT: string = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0).toString() + 'px';

/**
 * Set details, all set information recorded here. 
 * All updates trigger database update.
 * From here we use various dialogs, for gears, catch, coordinates.
 */
@Component({
    selector: 'app-set-detail',
    templateUrl: './set-detail.component.html',
    styleUrls: ['./set-detail.component.css'],
    standalone: false
})
export class SetDetailComponent implements OnInit {

  /**
   * The is the fishing event (set) the component is detailing
   */ 
  fishingEvent: FishingEvent = new FishingEvent('', '');
  setIndex!: number;
  isNewSet: boolean = false;
  isReadOnly: boolean = false;
  activeTrip?: Trip = new Trip();

  /**
   * If -1, this set is not part of the landing, otherwise it's the index into the landing sets array
   */ 
  partOfLandingInd: number = -1;
  
  /**
   * If not -1 then the set is part of an edited logbook
   */
  partOfEditedLanding: number = -1;

  /**
   * User gear present in the gear select dialog
   */ 
  userGear: Gear[] = [];

  firstGear: boolean = false;

  catchTableColumns: string[] = ['Species', 'RetainedCount', 'RetainedWeight', 'ReleasedCount', 'ReleasedWeight'];
  @ViewChild(MatTable) table!: MatTable<Catch>;

  /**
   * Subscribe to form update. All changes trigger database changes and updates.
   */
  formUpdateSubscription: Subscription = new Subscription();
  stringNumSubscription: Subscription = new Subscription();
  /* Form Fields */
  stringNum: FormControl = new FormControl('', [Validators.required, Validators.pattern('^[a-zA-Z0-9<>?\/!@#$%^&*()\. ]+$'), Validators.maxLength(10)]);
  setDetailForm: FormGroup = new FormGroup({
    gear: new FormControl(new Gear(), [Validators.required, gearValidator()]), //This is an object
    skateNum: new FormControl('', [Validators.required]), //This is for a gear
    lostPotNum: new FormControl(''),
    startDate: new FormControl('', [Validators.required, maxDateValidator()]),
    endDate: new FormControl('', [Validators.required, maxDateValidator()]),
    startLat: new FormControl(new Coordinate, [Validators.required, coordValidator(), shapefileLatValidator(this.fishingEvent.startLatManualEntry || this.fishingEvent.startLonManualEntry, this.fishingEvent.startLon)]),
    startLon: new FormControl(new Coordinate, [Validators.required, coordValidator(), shapefileLonValidator(this.fishingEvent.startLatManualEntry || this.fishingEvent.startLonManualEntry, this.fishingEvent.startLat)]),
    endLat: new FormControl(new Coordinate, [Validators.required, coordValidator(), shapefileLatValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLon)]),
    endLon: new FormControl(new Coordinate, [Validators.required, coordValidator(), shapefileLonValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLat)]),
    startDepth: new FormControl('', [Validators.required, Validators.min(3), Validators.max(700)]), //Only on non-pelagic gear
    endDepth: new FormControl('', [Validators.required, Validators.min(3), Validators.max(700)]), //Only on non-pelagic gear
    depthOffBottom: new FormControl(''), //Only on pelagic gear
    baitType: new FormControl('', [Validators.required]),
    comments: new FormControl('')
  }, {validators: skateNumLostPotValidator()});

  /**
   * For geolocation updates
   */
  locationSubscription: Subscription = new Subscription();

  parameterSubscription!: Subscription;

  /**
   * The coordinates returned from geolocation API
   */
  position!: GeolocationCoordinates;
  locationAllowed: boolean = false;

  /**
   * For use on lost skates/tubs/pots form field. Needed for validation purposes.
   */
  matcher = new MyErrorStateMatcher();

  disableAdd: boolean = false;

  userSetsSubscription!: Subscription; //Not for o.e.s

  //Edited landing subscription instead

  previousScreen: string = 'menu';

  errorNumber: number = 0; //For counting errors

  constructor(public _location: Location, public router: Router, public route: ActivatedRoute, public dialog: MatDialog, private readonly geolocation$: GeolocationService, private toastr: ToastrService, private insights: Insights) { }

  /**
   * Initialize here but most of it takes place in {@link SetDetailComponent.initialize}
   */
  ngOnInit(): void {
    LocalStorageService.storage_promise.then(_ => {
      console.log("Initializing SetDetailComponent ngOnInit");
      this.parameterSubscription = this.route.params.subscribe(_ => this.initialize());
      this.userGear = LocalStorageService.user_gear;
      this.locationSubscription = this.geolocation$.subscribe(pos => this.position = pos.coords, err => {
        
        // User has denied permission
        if (err.code == 1) {
          this.toastr.warning('Allow geolocation permission to access device location', 'Location Permission Denied');
          this.locationAllowed = false;
        }
      });
    }).then(() => {
      if (this.userSetsSubscription) this.userSetsSubscription.unsubscribe();
      //This runs before initialize function ends?
      let editedIndex = +this.route.snapshot.queryParams.editedIndex;
      if (isNaN(editedIndex) && editedIndex > -1) this.userSetsSubscription = LocalStorageService.edited_logbook$.subscribe(_ => this.initialize);
      else this.userSetsSubscription = LocalStorageService.user_trip$.subscribe(_ => this.initialize()); //edited landing subscription
    });
  }


  /**
   * Fill the form if editing a set and set dynamic updates on the set form.
   */
  initialize() {
    this.route.queryParams.subscribe(params => {
      // Disable the form if readOnly is set in the query params
      if (params['readOnly']) {
        this.setDetailForm.disable();
        this.stringNum.disable();
        this.isReadOnly = true;
      }
    });
    this.formUpdateSubscription.unsubscribe();
    this.stringNumSubscription.unsubscribe();
    this.previousScreen = this.route.snapshot.queryParams.previous ?? 'menu';
    this.setIndex = Number.parseInt(<string>this.route.snapshot.paramMap.get('index')); //route snapshot
    this.isNewSet = this.route.snapshot.paramMap.get('isNew') === 'true';

    let completedInd = +this.route.snapshot.queryParams.completedIndex;
    let editedIndex = +this.route.snapshot.queryParams.editedIndex;

    this.activeTrip = LocalStorageService.user_trip;

    if (!this.activeTrip) this.insights.trackTrace('SetDetailComponent: No active trip found');
    else if (!this.activeTrip!.sets[this.setIndex]) this.insights.trackTrace('SetDetailComponent: Missing set at index ' + this.setIndex.toString());

    if (completedInd > -1) this.fishingEvent = LocalStorageService.received_logbooks[completedInd].sets[this.setIndex];
    else if (this.setIndex > -1) this.fishingEvent = this.activeTrip!.sets[this.setIndex];
    else if (editedIndex > -1) this.fishingEvent = LocalStorageService.edited_logbook.GF.sets[editedIndex];

    if (this.activeTrip && this.setIndex >= this.activeTrip.sets.length && isNaN(completedInd)) {
      this.insights.trackTrace('SetDetailComponent: Set index out of range, navigating to menu');
      this.router.navigate(['menu']);
      return;
    } 

    this.partOfLandingInd = this.activeTrip.groundfishDelivery.sets.findIndex((s: FishingEvent) => s.fishingEventID === this.fishingEvent.fishingEventID);
    this.partOfEditedLanding = LocalStorageService.edited_logbook.GF.sets.findIndex((s: FishingEvent) => s.fishingEventID == this.fishingEvent.fishingEventID);

    let gearEntry: Gear =  this.fishingEvent.gear.type == 'Pot' ? PotGear.transformAnonymous(this.fishingEvent.gear) : 
                            this.fishingEvent.gear.type == 'Longline' ? LonglineGear.transformAnonymous(this.fishingEvent.gear) : 
                            PelagicGear.transformAnonymous(this.fishingEvent.gear);


    this.stringNum.setValue(this.fishingEvent.setNumber);
    this.setDetailForm.patchValue({
        gear: gearEntry,
        skateNum: this.fishingEvent.skateNum == 0 ? 0 : this.fishingEvent.skateNum ? this.fishingEvent.skateNum : '',
        lostPotNum: this.fishingEvent.lostPotCount == 0 ? 0 : this.fishingEvent.lostPotCount ? this.fishingEvent.lostPotCount : '',
        startDate: this.fishingEvent.startDate ? formatDate(this.fishingEvent.startDate, 'EEEE, MMM d, y, HH:mm', 'en-US'): '',
        endDate: this.fishingEvent.endDate ? formatDate(this.fishingEvent.endDate, 'EEEE, MMM d, y, HH:mm', 'en-US') : '',
        startLat: this.fishingEvent.startLat,
        startLon: this.fishingEvent.startLon,
        endLat: this.fishingEvent.endLat,
        endLon: this.fishingEvent.endLon,
        startDepth: this.fishingEvent.startDepth == 0 ? 0 : this.fishingEvent.startDepth ? this.fishingEvent.startDepth : '',
        endDepth: this.fishingEvent.endDepth == 0 ? 0 : this.fishingEvent.endDepth ? this.fishingEvent.endDepth : '',
        depthOffBottom: this.fishingEvent.depthOffBottom == 0 ? 0 : this.fishingEvent.depthOffBottom ? this.fishingEvent.depthOffBottom : '',
        baitType: this.fishingEvent.baitType ? this.fishingEvent.baitType : '',
        comments: this.fishingEvent.comments
      });

    if (this.fishingEvent.gear.type === "") this.firstGear = true;
    this.setDynamicValidation();
    this.subscribeToFormUpdate();
    this.calculateErrors();
  }


  calculateErrors() {
    this.errorNumber = 0;
    for (const control in this.setDetailForm.controls)
      for (const err in this.setDetailForm.controls[control].errors)
        this.errorNumber++;
    if (this.setDetailForm.errors) this.errorNumber++;
    if (this.stringNum.invalid) this.errorNumber++;
  }

  /**
   * Validation requirements change based on attached gear.
   * We clear and set validators on the form manually here.
   */
  setDynamicValidation() {
    this.formUpdateSubscription.unsubscribe();
    this.setDetailForm.get('endDate')?.setValidators([Validators.required, maxDateValidator(), setHaulDateValidator(this.fishingEvent.startDate)]);
    this.setDetailForm.get('endDate')?.updateValueAndValidity();

    this.setDetailForm.get('startLat')?.clearValidators();
    this.setDetailForm.get('startLat')?.setValidators([Validators.required, coordValidator(), shapefileLatValidator(this.fishingEvent.startLatManualEntry || this.fishingEvent.startLonManualEntry, this.fishingEvent.startLon)]);
    this.setDetailForm.get('startLat')?.updateValueAndValidity();
    this.setDetailForm.get('endLat')?.clearValidators();
    this.setDetailForm.get('endLat')?.setValidators([Validators.required, coordValidator(), shapefileLatValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLon)]);
    this.setDetailForm.get('endLat')?.updateValueAndValidity();
    this.setDetailForm.get('startLon')?.clearValidators();
    this.setDetailForm.get('startLon')?.setValidators([Validators.required, coordValidator(), shapefileLonValidator(this.fishingEvent.startLatManualEntry || this.fishingEvent.startLonManualEntry, this.fishingEvent.startLat)]);
    this.setDetailForm.get('startLon')?.updateValueAndValidity();
    this.setDetailForm.get('endLon')?.clearValidators();
    this.setDetailForm.get('endLon')?.setValidators([Validators.required, coordValidator(), shapefileLonValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLat)]);
    this.setDetailForm.get('endLon')?.updateValueAndValidity();

    if (this.fishingEvent.gear.type == 'Pelagic') {
      this.setDetailForm.get('skateNum')?.clearValidators();
      this.setDetailForm.get('lostPotNum')?.clearValidators();
      this.setDetailForm.get('startDepth')?.clearValidators();
      this.setDetailForm.get('endDepth')?.clearValidators();
      this.setDetailForm.get('depthOffBottom')?.setValidators([Validators.required, Validators.min(0)]);
      this.setDetailForm.get('skateNum')?.updateValueAndValidity()
      this.setDetailForm.get('lostPotNum')?.updateValueAndValidity()
      this.setDetailForm.get('startDepth')?.updateValueAndValidity()
      this.setDetailForm.get('endDepth')?.updateValueAndValidity()
      this.setDetailForm.get('depthOffBottom')?.updateValueAndValidity()
    } else {
      this.setDetailForm.get('lostPotNum')?.setValidators([Validators.required]);
      this.setDetailForm.get('lostPotNum')?.updateValueAndValidity();
      this.setDetailForm.get('depthOffBottom')?.clearValidators();
      this.setDetailForm.get('depthOffBottom')?.updateValueAndValidity();
      this.setDetailForm.get('skateNum')?.setValidators([Validators.required]);
      this.setDetailForm.get('skateNum')?.updateValueAndValidity()
      this.setDetailForm.get('startDepth')?.setValidators([Validators.required, Validators.min(3), Validators.max(700)]);
      this.setDetailForm.get('startDepth')?.updateValueAndValidity()
      this.setDetailForm.get('endDepth')?.setValidators([Validators.required, Validators.min(3), Validators.max(700)]);
      this.setDetailForm.get('endDepth')?.updateValueAndValidity()
    }
    this.calculateErrors();
  }


  /**
   * Certain fields to be cleared when switching gears
   */
  clearFormValues() {
    this.setDetailForm.patchValue({
      skateNum: '',
      lostPotNum: '',
      startDepth: '',
      endDepth: '',
      depthOffBottom: ''
    });
  }



  /**
   * Set subscriptions for form updates.
   */ 
  subscribeToFormUpdate() {
    this.stringNumSubscription.unsubscribe();
    this.stringNumSubscription = this.stringNum.valueChanges.subscribe((str: string) => {
      if (this.stringNum.pristine) {
        this.stringNum.markAsDirty();
      }
      this.fishingEvent.setNumber = str;
      this.updateLandingAndEvent();
      this.calculateErrors();
    });

    this.formUpdateSubscription.unsubscribe();
    this.formUpdateSubscription = this.setDetailForm.valueChanges.subscribe(value => {
      if (this.setDetailForm.pristine) {
        this.setDetailForm.markAsDirty();
      }
      this.fishingEvent.update(value);
      // If this event is part of the landing, update the landing too.
      this.updateLandingAndEvent();
      this.calculateErrors();
    });
  }



  /**
   * Database updates to current landing and this set.
   */
  updateLandingAndEvent() {
    if (this.partOfLandingInd != -1) {
      this.activeTrip!.groundfishDelivery.sets[this.partOfLandingInd] = this.fishingEvent;
    } else if (this.setDetailForm.get('endDate')?.valid && this.stringNum.valid) {
      this.activeTrip!.groundfishDelivery.sets.push(this.fishingEvent);
      this.partOfLandingInd = this.activeTrip!.groundfishDelivery.sets.length - 1;     
    }

    if (this.partOfEditedLanding != -1) {
      LocalStorageService.edited_logbook.GF.sets[this.partOfEditedLanding] = this.fishingEvent;
      LocalStorageService.updateLanding(LocalStorageService.edited_logbook.GF).then((res: StorageResult) => {
        if (!res.success) {
          this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');
          this.insights.trackException(res.error);
        }
      });
    }

    if (this.setIndex != -1) { 
      LocalStorageService.updateTrip().then((res: StorageResult) => {
        if (!res.success) {
          this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');        
          this.insights.trackException(res.error);
        }
      });
    }
  }


  confirmDelete() { //For o.e.s?
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: {screen: 'deleteset'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteSet();
      }     
    });
  }


  /**
   * Open the gear select dialog and set the returned gear on the gear field.
   * A gear may not be returned, so do nothing then.
   */
  addGear() {

    // dialog dimensions
    let w: string = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0).toString() + 'px';
    let h: string = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0).toString() + 'px';

    const dialogRef: MatDialogRef<GearSelectDialogComponent, any> = this.dialog.open(GearSelectDialogComponent, {
      width: SCREEN_WIDTH > w ? SCREEN_WIDTH : w,
      height: SCREEN_HEIGHT > h ? SCREEN_HEIGHT : h,
      data: { screen: "List", gearIndex: -1, setType: 'Groundfish' }
    });

    // If nothing is returned, don't change gear BUT still need to see if gear was updated!

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result >= 0) {
        // Gear was selected

        if (this.firstGear) { //don't show the confirmation if it's the first time selecting gear
          this.selectGear(result);
          this.firstGear = false;
        } else {
          const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            autoFocus: false,
            data: { screen: 'changegear' },
          });

          dialogRef.afterClosed().subscribe(confirmResult => {
            if (confirmResult) {
              this.selectGear(result);
            }
          });
        }
      }
    });
  }

  selectGear(result: any) {
    this.formUpdateSubscription.unsubscribe();
    let groundfishGears: Gear[] = LocalStorageService.user_gear.filter(g => g.fishery == "Groundfish");
    this.fishingEvent.gear = groundfishGears[result];
    this.setDynamicValidation();
    this.clearFormValues();
    this.subscribeToFormUpdate();
    this.setDetailForm.get('gear')?.setValue(groundfishGears[result]);
  }


  /**
   * For manually entering of coordinates through the CoordinateDialogComponent.
   * @param title Editing latitude or longitude?
   */
  openCoordinateDialog(title: string) {
    let w: string = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0).toString() + 'px';
    let value: Coordinate = this.setDetailForm.get(title)?.value;
    const dialogRef = this.dialog.open(CoordinateDialogComponent, {
      width: SCREEN_WIDTH > w ? SCREEN_WIDTH : w,
      data: {title: title, coord: value, fishery: 'GF'},
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        console.log(result)
        switch (title) {
          case 'startLat':
            this.fishingEvent.startLatManualEntry = true;
            break;
          case 'startLon':
            this.fishingEvent.startLonManualEntry = true;
            break;
          case 'endLat':
            this.fishingEvent.endLatManualEntry = true;
            break;
          case 'endLon':
            this.fishingEvent.endLonManualEntry = true;
            break;
        }
        value = result;
        value.fillDecimal();
        this.formUpdateSubscription.unsubscribe();
        this.setDetailForm.get(title)?.setValue(value);            
        this.fishingEvent.update(this.setDetailForm.value);
        //Race condition - will the value changes event complete before the setDynamicValidation method activates?    
        this.setDynamicValidation(); 
        this.subscribeToFormUpdate();
        this.setDetailForm.get(title)?.updateValueAndValidity(); 
      }     
    });
  }


  /**
   * For auto-filling of coordinates and date/time on the set.
   * We have to keep track of whether coordinate data is auto filled or manually entered.
   */
  fillSetTimeLocation() {   
    if (!this.locationAllowed || !this.position) {
      this.locationSubscription = this.geolocation$.subscribe(pos => { this.position = pos.coords; this.locationAllowed = true; }, err => {

        // User has denied permission
        if (err.code == 1) {
          this.toastr.warning('Allow geolocation permission to access device location', 'Location Permission Denied');
          this.locationAllowed = false;
          return;
        }
      });
    }

    let fillFunction =  () => {
      this.formUpdateSubscription.unsubscribe();
      this.stringNumSubscription.unsubscribe();
      this.setDetailForm.get('startLat')?.setValue(Coordinate.fillDegrees(this.position.latitude));
      this.setDetailForm.get('startLon')?.setValue(Coordinate.fillDegrees(this.position.longitude));
      this.setDetailForm.get('startDate')?.setValue(formatDate(new Date(), 'EEEE, MMM d, y, HH:mm', 'en-US'));
  
      this.fishingEvent.startLatManualEntry = false;
      this.fishingEvent.startLonManualEntry = false;   
  
      this.fishingEvent.update(this.setDetailForm.value);

      if (this.partOfLandingInd != -1) LocalStorageService.user_trip.groundfishDelivery.sets[this.partOfLandingInd] = this.fishingEvent;    
      if (this.partOfEditedLanding != -1) LocalStorageService.edited_logbook.GF.sets[this.partOfEditedLanding] = this.fishingEvent;
         
      this.setDetailForm.get('startLat')?.setValidators([Validators.required, coordValidator(), shapefileLatValidator(this.fishingEvent.startLatManualEntry || this.fishingEvent.startLonManualEntry, this.fishingEvent.startLon)]);
      this.setDetailForm.get('startLat')?.updateValueAndValidity()
      this.setDetailForm.get('startLon')?.setValidators([Validators.required, coordValidator(), shapefileLonValidator(this.fishingEvent.startLatManualEntry || this.fishingEvent.startLonManualEntry, this.fishingEvent.startLat)]);
      this.setDetailForm.get('startLon')?.updateValueAndValidity();
      this.setDetailForm.get('endDate')?.setValidators([Validators.required, maxDateValidator(), setHaulDateValidator(this.setDetailForm.get('startDate')?.value)]);
      this.subscribeToFormUpdate();
      this.setDetailForm.get('endDate')?.updateValueAndValidity();
    };

    if (this.fishingEvent.startLat.isUnfilled() && this.fishingEvent.startLon.isUnfilled() && !this.fishingEvent.startDate) {
      fillFunction();
      return;
    }

    this.dialog.open(ConfirmDialogComponent, {
      data: {screen: 'autofill'}
    }).afterClosed().subscribe(result => {
      if (result) fillFunction();
    });   
  }

  /**
   * For auto-filling of coordinates and date/time on the haul portion.
   * We have to keep track of whether coordinate data is auto filled or manually entered.
   */
  fillHaulTimeLocation(dateOnly = false) {
    if (!this.locationAllowed || this.position) {
      this.locationSubscription = this.geolocation$.subscribe(pos => { this.position = pos.coords; this.locationAllowed = true; }, err => {

        // User has denied permission
        if (err.code == 1) {
          this.toastr.warning('Allow geolocation permission to access device location', 'Location Permission Denied');
          this.locationAllowed = false;
        }
      });
    }

    let isPelagic = this.fishingEvent.gear.type == 'Pelagic';

    let fillFunction = () => {
      this.formUpdateSubscription.unsubscribe();
      this.stringNumSubscription.unsubscribe();

      if (dateOnly && !isPelagic) {
        this.setDetailForm.get('endDate')?.setValue(formatDate(new Date(), 'EEEE, MMM d, y, HH:mm', 'en-US'));
      } else if (!dateOnly && !isPelagic) {
        console.log("Set haul coordinates")
        this.setDetailForm.get('endLat')?.setValue(Coordinate.fillDegrees(this.position.latitude));
        this.setDetailForm.get('endLon')?.setValue(Coordinate.fillDegrees(this.position.longitude));
        this.fishingEvent.endLatManualEntry = false;
        this.fishingEvent.endLonManualEntry = false;
      } else if (isPelagic) {
        this.setDetailForm.get('endLat')?.setValue(Coordinate.fillDegrees(this.position.latitude));
        this.setDetailForm.get('endLon')?.setValue(Coordinate.fillDegrees(this.position.longitude));
        this.setDetailForm.get('endDate')?.setValue(formatDate(new Date(), 'EEEE, MMM d, y, HH:mm', 'en-US'));
        this.fishingEvent.endLatManualEntry = false;
        this.fishingEvent.endLonManualEntry = false;
      }

      if (this.partOfLandingInd != -1) this.activeTrip!.groundfishDelivery.sets[this.partOfLandingInd] = this.fishingEvent;
      if (this.partOfEditedLanding != -1) LocalStorageService.edited_logbook.GF.sets[this.partOfEditedLanding] = this.fishingEvent;
        this.setDetailForm.get('endLat')?.setValidators([Validators.required, coordValidator(), shapefileLatValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLon)]);
        this.setDetailForm.get('endLat')?.updateValueAndValidity()
        this.setDetailForm.get('endLon')?.setValidators([Validators.required, coordValidator(), shapefileLonValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLat)]);
        this.setDetailForm.get('endLon')?.updateValueAndValidity();
        this.subscribeToFormUpdate();
        this.setDetailForm.updateValueAndValidity();
    }


    if ((dateOnly && !this.fishingEvent.endDate && !isPelagic) || 
        (!dateOnly && this.fishingEvent.endLat.isUnfilled() && this.fishingEvent.endLon.isUnfilled() && !isPelagic) || 
        (this.fishingEvent.endLat.isUnfilled() && this.fishingEvent.endLon.isUnfilled() && !this.fishingEvent.endDate && isPelagic)) {
          fillFunction();
          return;
    }

    this.dialog.open(ConfirmDialogComponent, {
      data: {screen: 'autofill'}
    }).afterClosed().subscribe(result => {
      if (result) fillFunction();      
    });
        
  }

  /**
   * Manual date/time editing through the DateTimePickerComponent
   * @param startOrEnd Start or end date
   */
  editDateTime(startOrEnd: string) {
    let dateInput = this.setDetailForm.get(startOrEnd)?.value == '' ? new Date() : new Date(this.setDetailForm.get(startOrEnd)?.value);
    let start: boolean = startOrEnd.toLowerCase() == "startdate";

    const dialogRef = this.dialog.open(DateTimePickerComponent, {
      width: '500px',
      autoFocus: false,
      data: { date: dateInput, isDelivery: false, isHMS: false, isStart: start, isEnd: !start },
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        //Adjusting the validation here gives a pouchDB update conflict since a call to the update subscription gets triggered twice
        this.formUpdateSubscription.unsubscribe();
        this.stringNumSubscription.unsubscribe();
        this.setDetailForm.get(startOrEnd)?.setValue(formatDate(result, 'EEEE, MMM d, y, HH:mm', 'en-US'));
        this.subscribeToFormUpdate();
        this.setDetailForm.get('endDate')?.setValidators([Validators.required, maxDateValidator(), setHaulDateValidator(this.setDetailForm.get('startDate')?.value)]);
        this.setDetailForm.get('endDate')?.updateValueAndValidity();
      }     
    });
  }

  /**
   * Open the catch dialog to add fish species
   */ 
  openCatchDialog(catchIndex: number): void {

    if (this.isReadOnly) return;
    let isNew = false;
    if (catchIndex >= this.fishingEvent.catchItems.length) {
      this.fishingEvent.catchItems.push(new Catch(''));
      isNew = true;
    }

    const dialogRef = this.dialog.open(AddCatchDialogComponent, {
      autoFocus: false,
      width: '400px',
      height: '600px',
      position: {top: '5px'},
      disableClose: true,
      data: {catch: this.fishingEvent.catchItems[catchIndex], fishery: 'Groundfish'} //empty if row clicked
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.delete) {
          this.fishingEvent.catchItems.splice(catchIndex, 1);
        } else {
          if (catchIndex >= this.fishingEvent.catchItems.length) this.fishingEvent.catchItems.push(new Catch(''));
          let item = this.fishingEvent.catchItems[catchIndex];
          item.species = result.species;
          item.retainedCount = Number.parseInt(result.retainedCount);
          item.retainedCount = item.retainedCount ? item.retainedCount : 0;
          item.retainedWeight = Number.parseInt(result.retainedWeight);
          item.retainedWeight = item.retainedWeight ? item.retainedWeight : 0;
          item.releasedCount = Number.parseInt(result.releasedCount);
          item.releasedCount = item.releasedCount ? item.releasedCount : 0;
          item.releasedWeight = Number.parseInt(result.releasedWeight);
          item.releasedWeight = item.releasedWeight ? item.releasedWeight : 0;
        }
        this.table.renderRows();

        if (this.partOfLandingInd != -1) this.activeTrip!.groundfishDelivery.sets[this.partOfLandingInd] = this.fishingEvent;
        if (this.partOfEditedLanding != -1) {
          LocalStorageService.edited_logbook.GF.sets[this.partOfEditedLanding] = this.fishingEvent;
          this.updateLandingAndEvent();
        }
        LocalStorageService.updateTrip().then((res: StorageResult) => {
          if (!res.success) {
            this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');
            this.insights.trackException(res.error);
          }
        });
      } else if (isNew) {
        this.fishingEvent.catchItems.splice(catchIndex, 1);
      }
      
    });
  }

  /**
   * Delete the current set
   */ 
  async deleteSet(): Promise<boolean> {
    // If no valid set exists, resolve immediately.
    if (this.setIndex === -1 && this.partOfEditedLanding === -1) {
      return Promise.resolve(true);
    }

    const res = await LocalStorageService.deleteFishingEvent(this.fishingEvent.fishingEventID, this.partOfEditedLanding !== -1, 'GF');
    if (res) {
      console.log("going to menu");
      if (this.partOfEditedLanding !== -1) {
        this.navigateToDelivery();
      } else {
        this.router.navigate(['menu']);
      }
      return true;
    } else {
      alert('Deletion failed');
      return false;
    }
  }



  /**
   * Navigate to previous set in set list
   * @returns 
   */
  previousSet() {
    let ind = -1;
    for (let i = this.setIndex - 1; i >= 0; i--) {
      if (this.activeTrip?.sets[i].fishery === 'Groundfish') {
        ind = i;
        break;
      }
    }

    if (this.setIndex == 0 || this.setIndex == -1 || ind == -1) return;
      
    this.partOfLandingInd = -1;
    this.partOfEditedLanding = -1;

    this.router.navigate(['set_detail', ind, false]);

    if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
    this.setDetailForm.markAsUntouched();
  }


  /**
   * Create a new set at end of set list and go to it.
   */
  addSet() {
    if (!LocalStorageService.storage_open) return;
    let fs = new FishingEvent('', "Groundfish");
    fs.fishingEventID = 'Set-' + (MenuComponent.set_num++).toString();

    LocalStorageService.storeFishingEvent(fs).then(res => {
      if (res.success) {
        this.partOfLandingInd = -1;
        this.partOfEditedLanding = -1;
        this.router.navigate(['set_detail', this.activeTrip?.sets.findIndex(set => set.fishingEventID == res.data), true]);
      } else {
        this.toastr.error('Error creating new set', 'Set Creation Error');
        this.insights.trackException(res.error);
      }
      
      this.disableAdd = false;
    }).catch(_ => this.disableAdd = false); 
    
    if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
    this.setDetailForm.markAsUntouched();
  }


  /**
   * Navigate to next set in set list
   * @returns 
   */
  nextSet() {
    let ind = this.activeTrip!.sets.findIndex(s => s.fishingEventID == this.fishingEvent.fishingEventID);
    for (let i = ind + 1; i < this.activeTrip!.sets.length; i++) {
      if (this.activeTrip?.sets[i].fishery === 'Groundfish') {
        ind = i;
        break;
      }
    }
    if (ind == this.setIndex) return;

    this.partOfEditedLanding = -1;
    this.partOfLandingInd = -1;
    this.router.navigate(['set_detail', ind, false]);

    if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
    this.setDetailForm.markAsUntouched();
  }


  navigateToDelivery() {
    if (this.setIndex == -1 && this.partOfEditedLanding != -1) {
      this.router.navigate(['delivery_detail', 2, LocalStorageService.received_logbooks.findIndex(rl => rl.sets.some(s => s.fishingEventID == this.fishingEvent.fishingEventID))], { queryParams: { previous: 'set_detail', setIndex: this.setIndex } })
    } else if (this.setIndex != -1) {
      this.router.navigate(['delivery_detail', 0, -1], { queryParams: { previous: 'set_detail', setIndex: this.setIndex } });
    }
  }


  ngOnDestroy() {
    if (this.formUpdateSubscription) this.formUpdateSubscription.unsubscribe();
    if (this.stringNumSubscription) this.stringNumSubscription.unsubscribe();
    if (this.locationSubscription) this.locationSubscription.unsubscribe();
    if (this.userSetsSubscription) this.userSetsSubscription.unsubscribe();
    if (this.parameterSubscription) this.parameterSubscription.unsubscribe();
    //Edited landing subscription
  }


  /**
   * Highlight fields that are in an error state.
   */
  openErrorDialog() {   
    //Looks like adding/removing validators is the solution here, need to finesse a bit though since this adds but doesn't remove
    this.setDetailForm.get('endDate')?.setValidators([Validators.required, maxDateValidator(), setHaulDateValidator(this.fishingEvent.startDate)]);
    this.setDetailForm.get('endDate')?.updateValueAndValidity();
    this.setDetailForm.markAllAsTouched();
    console.log(this.setDetailForm.errors)
    
    //Print to console all errors on the form
    for (const controlName in this.setDetailForm.controls) {    
      const control = this.setDetailForm.get(controlName);
      if (control && control.errors) {
        console.log(`Errors in ${controlName}:`, control.errors);
      }
    }

    if (this.setDetailForm.valid && this.stringNum.valid)
      this.toastr.success('No errors on this set', 'Valid');
    else
      this.toastr.warning('Errors on this set, see fields', 'Invalid');
  }


  _forbidNonNumeric(control: AbstractControl) {
    control.setValue(control.value.replace(/[^0-9]/g, ''), {onlySelf: true, emitEvent: false});
  }

  /**
 * Checks if the set is new and untouched, and deletes it if so before navigating away.
 * @param targetRoute The route to navigate to (e.g., 'menu' or home).
 */
  handleExit(targetRoute: string) {
    if (this.isNewSet && this.setDetailForm.pristine && this.stringNum.pristine) {
      // Call deleteSet (navigation done within deleteSet)
      this.deleteSet().then((res) => {
        if (res) {
          console.log('New, untouched set deleted before navigation.');
        } else {
          // If deletion failed, you may wish to still navigate or alert the user.
          console.error('Deletion failed.');
        }
      });
    } else {
        // If the form has been touched (or it’s an existing set), just navigate.
        this.router.navigate([targetRoute], { queryParams: { fishery: 0 } });
    }
  }


}
